import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Pencil,
  Trash2,
  ShieldCheck,
  Loader2,
  Search,
  Flag,
  ExternalLink,
  Calendar,
  CheckCircle2,
  ArrowUpDown,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

async function logEdit(args: {
  program_id: string | null;
  action: string;
  changes?: Record<string, unknown>;
}) {
  const { data: u } = await supabase.auth.getUser();
  const user = u.user;
  if (!user) return;
  await supabase.from("program_edit_log").insert({
    program_id: args.program_id,
    edited_by: user.id,
    edited_by_email: user.email ?? null,
    action: args.action,
    changes: (args.changes ?? {}) as never,
  } as never);
}

type ProgramStatus =
  | "active"
  | "closing_soon"
  | "closed"
  | "next_cycle_expected"
  | "needs_review"
  | "draft";

type Program = {
  id: string;
  title: string;
  category: string | null;
  eligibility: string | null;
  description: string | null;
  official_url: string | null;
  apply_url: string | null;
  application_deadline: string | null;
  is_recurring: boolean;
  status: ProgramStatus;
  last_verified_at: string | null;
  is_trending: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

const statusOptions: { value: ProgramStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "closing_soon", label: "Closing Soon" },
  { value: "closed", label: "Expired (Closed)" },
  { value: "next_cycle_expected", label: "Next Cycle Expected" },
  { value: "needs_review", label: "Needs Review" },
  { value: "draft", label: "Draft" },
];

type SortKey =
  | "updated_desc"
  | "deadline_asc"
  | "deadline_desc"
  | "verified_asc"
  | "verified_desc"
  | "status";

const sortOptions: { value: SortKey; label: string }[] = [
  { value: "updated_desc", label: "Recently updated" },
  { value: "deadline_asc", label: "Deadline (soonest)" },
  { value: "deadline_desc", label: "Deadline (latest)" },
  { value: "verified_asc", label: "Last verified (oldest)" },
  { value: "verified_desc", label: "Last verified (newest)" },
  { value: "status", label: "Status" },
];

const statusOrder: Record<ProgramStatus, number> = {
  needs_review: 0,
  closing_soon: 1,
  active: 2,
  next_cycle_expected: 3,
  closed: 4,
  draft: 5,
};

function statusBadge(s: ProgramStatus) {
  const map: Record<ProgramStatus, { label: string; cls: string }> = {
    active: { label: "Active", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    closing_soon: { label: "Closing Soon", cls: "bg-orange-100 text-orange-800 border-orange-200" },
    closed: { label: "Expired", cls: "bg-red-100 text-red-800 border-red-200" },
    next_cycle_expected: { label: "Next Cycle Expected", cls: "bg-sky-100 text-sky-800 border-sky-200" },
    needs_review: { label: "Needs Review", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  };
  const m = map[s];
  return <Badge className={m.cls}>{m.label}</Badge>;
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

const empty: Partial<Program> = {
  title: "",
  category: "",
  eligibility: "",
  description: "",
  official_url: "",
  apply_url: "",
  application_deadline: "",
  is_recurring: false,
  status: "active",
};

export function ProgramsPanel() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [staleOnly, setStaleOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("updated_desc");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<Program> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: programs = [], isLoading } = useQuery({
    queryKey: ["admin-programs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("programs")
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data as Program[];
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async (p: Partial<Program>) => {
      const payload = {
        title: (p.title ?? "").trim(),
        category: p.category?.trim() || null,
        eligibility: p.eligibility?.trim() || null,
        description: p.description?.trim() || null,
        official_url: p.official_url?.trim() || null,
        apply_url: p.apply_url?.trim() || null,
        application_deadline: p.application_deadline || null,
        is_recurring: !!p.is_recurring,
        status: (p.status || "active") as ProgramStatus,
        notes: p.notes?.trim() || null,
      };
      if (p.id) {
        const { error } = await supabase.from("programs").update(payload).eq("id", p.id);
        if (error) throw error;
        await logEdit({ program_id: p.id, action: "update", changes: payload });
      } else {
        const { data, error } = await supabase
          .from("programs")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        await logEdit({
          program_id: data?.id ?? null,
          action: "create",
          changes: payload,
        });
      }
    },
    onSuccess: () => {
      toast.success("Program saved");
      qc.invalidateQueries({ queryKey: ["admin-programs"] });
      qc.invalidateQueries({ queryKey: ["home-dynamic-programs"] });
      setDialogOpen(false);
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message || "Failed to save"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("programs").delete().eq("id", id);
      if (error) throw error;
      await logEdit({ program_id: id, action: "delete" });
    },
    onSuccess: () => {
      toast.success("Program deleted");
      qc.invalidateQueries({ queryKey: ["admin-programs"] });
      qc.invalidateQueries({ queryKey: ["home-dynamic-programs"] });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (id: string) => {
      const verifiedAt = new Date().toISOString();
      const { error } = await supabase
        .from("programs")
        .update({ last_verified_at: verifiedAt })
        .eq("id", id);
      if (error) throw error;
      await logEdit({
        program_id: id,
        action: "mark_verified",
        changes: { last_verified_at: verifiedAt },
      });
    },
    onSuccess: () => {
      toast.success("Marked verified");
      qc.invalidateQueries({ queryKey: ["admin-programs"] });
    },
  });

  const flagMutation = useMutation({
    mutationFn: async (program: Program) => {
      const { error: pErr } = await supabase
        .from("programs")
        .update({ status: "needs_review" })
        .eq("id", program.id);
      if (pErr) throw pErr;
      const { error: fErr } = await supabase.from("flagged_items").insert({
        program_id: program.id,
        reason: "missing_data",
        details: `"${program.title}" manually flagged for review by admin.`,
      });
      if (fErr) throw fErr;
      await logEdit({
        program_id: program.id,
        action: "flag",
        changes: { reason: "missing_data" },
      });
    },
    onSuccess: () => {
      toast.success("Program flagged for review");
      qc.invalidateQueries({ queryKey: ["admin-programs"] });
      qc.invalidateQueries({ queryKey: ["flagged-items"] });
    },
  });

  // Counts (computed from full dataset, ignoring filters)
  const counts = {
    active: programs.filter((p) => p.status === "active").length,
    closing_soon: programs.filter((p) => p.status === "closing_soon").length,
    closed: programs.filter((p) => p.status === "closed").length,
    needs_review: programs.filter((p) => p.status === "needs_review").length,
    stale: programs.filter((p) => {
      const d = daysSince(p.last_verified_at);
      return d === null || d > 30;
    }).length,
  };

  // Distinct categories for category filter
  const categories = Array.from(
    new Set(
      programs
        .map((p) => (p.category ?? "").trim())
        .filter((c) => c.length > 0)
    )
  ).sort((a, b) => a.localeCompare(b));

  const filtered = programs
    .filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (categoryFilter !== "all" && (p.category ?? "") !== categoryFilter)
        return false;
      if (staleOnly) {
        const d = daysSince(p.last_verified_at);
        if (d !== null && d <= 30) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = [
          p.title,
          p.category ?? "",
          p.eligibility ?? "",
          p.official_url ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortKey) {
        case "deadline_asc":
        case "deadline_desc": {
          const av = a.application_deadline
            ? new Date(a.application_deadline).getTime()
            : Number.POSITIVE_INFINITY;
          const bv = b.application_deadline
            ? new Date(b.application_deadline).getTime()
            : Number.POSITIVE_INFINITY;
          return sortKey === "deadline_asc" ? av - bv : bv - av;
        }
        case "verified_asc":
        case "verified_desc": {
          const av = a.last_verified_at
            ? new Date(a.last_verified_at).getTime()
            : 0;
          const bv = b.last_verified_at
            ? new Date(b.last_verified_at).getTime()
            : 0;
          return sortKey === "verified_asc" ? av - bv : bv - av;
        }
        case "status":
          return statusOrder[a.status] - statusOrder[b.status];
        case "updated_desc":
        default:
          return (
            new Date(b.updated_at).getTime() -
            new Date(a.updated_at).getTime()
          );
      }
    });

  const resetFilters = () => {
    setStatusFilter("all");
    setCategoryFilter("all");
    setStaleOnly(false);
    setSearch("");
    setSortKey("updated_desc");
  };
  const filtersActive =
    statusFilter !== "all" ||
    categoryFilter !== "all" ||
    staleOnly ||
    search.length > 0 ||
    sortKey !== "updated_desc";

  const openCreate = () => {
    setEditing({ ...empty });
    setDialogOpen(true);
  };
  const openEdit = (p: Program) => {
    setEditing({ ...p });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Programs</h2>
          <p className="text-sm text-muted-foreground">
            Manage program listings used by the homepage and weekly maintenance scan.
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2 self-start">
          <Plus className="h-4 w-4" />
          Add program
        </Button>
      </div>

      {/* Quick count chips */}
      <div className="flex flex-wrap gap-2">
        <CountChip
          label="Active"
          count={counts.active}
          active={statusFilter === "active"}
          tone="emerald"
          onClick={() =>
            setStatusFilter(statusFilter === "active" ? "all" : "active")
          }
        />
        <CountChip
          label="Closing Soon"
          count={counts.closing_soon}
          active={statusFilter === "closing_soon"}
          tone="orange"
          onClick={() =>
            setStatusFilter(
              statusFilter === "closing_soon" ? "all" : "closing_soon"
            )
          }
        />
        <CountChip
          label="Expired"
          count={counts.closed}
          active={statusFilter === "closed"}
          tone="red"
          onClick={() =>
            setStatusFilter(statusFilter === "closed" ? "all" : "closed")
          }
        />
        <CountChip
          label="Needs Review"
          count={counts.needs_review}
          active={statusFilter === "needs_review"}
          tone="amber"
          onClick={() =>
            setStatusFilter(
              statusFilter === "needs_review" ? "all" : "needs_review"
            )
          }
        />
        <CountChip
          label="Stale (>30d)"
          count={counts.stale}
          active={staleOnly}
          tone="slate"
          icon={<Clock className="h-3 w-3" />}
          onClick={() => setStaleOnly(!staleOnly)}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, category, eligibility, source URL..."
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
          <SelectTrigger className="w-full sm:w-[210px]">
            <ArrowUpDown className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Showing {filtered.length} of {programs.length}
        </span>
        {filtersActive && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={resetFilters}
          >
            Clear filters
          </Button>
        )}
      </div>

      {isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto my-12" />
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Calendar className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium text-foreground">No programs yet</p>
            <p className="text-sm">
              Click "Add program" to create your first listing.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => {
            const verifiedDays = daysSince(p.last_verified_at);
            const stale = verifiedDays !== null && verifiedDays > 30;
            const highlight =
              p.status === "closed" || p.status === "needs_review";
            return (
              <Card
                key={p.id}
                className={
                  highlight
                    ? "border-amber-300 bg-amber-50/40"
                    : ""
                }
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {statusBadge(p.status)}
                        {p.category && (
                          <Badge variant="sector">{p.category}</Badge>
                        )}
                        {p.is_recurring && (
                          <Badge variant="outline">Recurring</Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-foreground">{p.title}</h3>
                      {p.eligibility && (
                        <p className="text-xs text-muted-foreground mt-1">
                          <span className="font-medium text-foreground/80">Eligibility:</span>{" "}
                          {p.eligibility}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                        {p.application_deadline && (
                          <span>
                            <Calendar className="inline h-3 w-3 mr-1" />
                            Deadline:{" "}
                            <span className="text-foreground">
                              {new Date(p.application_deadline).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                          </span>
                        )}
                        <span className={stale ? "text-amber-700 font-medium" : ""}>
                          <ShieldCheck className="inline h-3 w-3 mr-1" />
                          Last verified:{" "}
                          {p.last_verified_at
                            ? `${verifiedDays}d ago`
                            : "never"}
                        </span>
                        {p.official_url && (
                          <a
                            href={p.official_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline inline-flex items-center gap-1"
                          >
                            Source <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex sm:flex-col gap-2 shrink-0 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => verifyMutation.mutate(p.id)}
                        disabled={verifyMutation.isPending}
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Verify
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => openEdit(p)}
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={() => flagMutation.mutate(p)}
                        disabled={flagMutation.isPending}
                      >
                        <Flag className="h-4 w-4" />
                        Flag
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => {
                          if (confirm(`Delete "${p.title}"?`)) deleteMutation.mutate(p.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ProgramDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditing(null);
        }}
        program={editing}
        onChange={setEditing}
        onSave={() => editing && upsertMutation.mutate(editing)}
        saving={upsertMutation.isPending}
      />
    </div>
  );
}

function ProgramDialog({
  open,
  onOpenChange,
  program,
  onChange,
  onSave,
  saving,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  program: Partial<Program> | null;
  onChange: (p: Partial<Program>) => void;
  onSave: () => void;
  saving: boolean;
}) {
  if (!program) return null;
  const set = (k: keyof Program, v: unknown) => onChange({ ...program, [k]: v });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{program.id ? "Edit program" : "Add program"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Program name *</Label>
            <Input
              value={program.title ?? ""}
              onChange={(e) => set("title", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Input
                placeholder="e.g. Government, Internship, Online Course"
                value={program.category ?? ""}
                onChange={(e) => set("category", e.target.value)}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={(program.status as string) ?? "active"}
                onValueChange={(v) => set("status", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions
                    .filter((o) => o.value !== "all")
                    .map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Eligibility</Label>
            <Textarea
              rows={2}
              placeholder="e.g. Indian citizens, age 18-25, completed 12th"
              value={program.eligibility ?? ""}
              onChange={(e) => set("eligibility", e.target.value)}
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={program.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Application deadline</Label>
              <Input
                type="date"
                value={program.application_deadline ?? ""}
                onChange={(e) => set("application_deadline", e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="recurring"
                checked={!!program.is_recurring}
                onChange={(e) => set("is_recurring", e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="recurring" className="cursor-pointer">
                Recurring program
              </Label>
            </div>
          </div>
          <div>
            <Label>Source URL (official)</Label>
            <Input
              type="url"
              placeholder="https://..."
              value={program.official_url ?? ""}
              onChange={(e) => set("official_url", e.target.value)}
            />
          </div>
          <div>
            <Label>Apply URL</Label>
            <Input
              type="url"
              placeholder="https://..."
              value={program.apply_url ?? ""}
              onChange={(e) => set("apply_url", e.target.value)}
            />
          </div>
          <div>
            <Label>Internal notes</Label>
            <Textarea
              rows={2}
              value={program.notes ?? ""}
              onChange={(e) => set("notes", e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving || !program.title?.trim()}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function CountChip({
  label,
  count,
  active,
  tone,
  icon,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  tone: "emerald" | "orange" | "red" | "amber" | "slate";
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  const tones: Record<string, { active: string; idle: string }> = {
    emerald: {
      active: "bg-emerald-600 text-white border-emerald-600",
      idle: "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100",
    },
    orange: {
      active: "bg-orange-600 text-white border-orange-600",
      idle: "bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100",
    },
    red: {
      active: "bg-red-600 text-white border-red-600",
      idle: "bg-red-50 text-red-800 border-red-200 hover:bg-red-100",
    },
    amber: {
      active: "bg-amber-600 text-white border-amber-600",
      idle: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100",
    },
    slate: {
      active: "bg-slate-700 text-white border-slate-700",
      idle: "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100",
    },
  };
  const cls = active ? tones[tone].active : tones[tone].idle;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${cls}`}
    >
      {icon}
      <span>{label}</span>
      <span
        className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-[11px] font-semibold ${
          active ? "bg-white/20" : "bg-white/70"
        }`}
      >
        {count}
      </span>
    </button>
  );
}
